import { useState } from 'react';
import { Heading, BodyText, Card, Input, Button, Switch } from '@aaimplatform/ui';
import { User, Mail, Shield, Bell, Lock } from 'lucide-react';
import { useAuthStore } from '../stores/auth';

const NOTIFICATION_PREFS = [
  { key: 'cha_escalation', label: 'CHA tier escalation alerts', description: 'Receive alerts when collateral health changes tier', defaultOn: true },
  { key: 'loan_applications', label: 'New loan applications', description: 'Notification when new PAL applications are submitted', defaultOn: true },
  { key: 'compliance_reports', label: 'Compliance reports', description: 'Weekly compliance summary digest', defaultOn: false },
  { key: 'system_maintenance', label: 'System maintenance', description: 'Scheduled downtime and update notifications', defaultOn: true },
];

function NotificationPreferences() {
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NOTIFICATION_PREFS.map(p => [p.key, p.defaultOn])),
  );

  const toggle = (key: string) => setPrefs(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <Card>
      <div className="p-6 space-y-4">
        <Heading level={3} size="compact" className="font-sans mb-4">Notification Preferences</Heading>
        {NOTIFICATION_PREFS.map(pref => (
          <div key={pref.key} className="flex items-center justify-between py-3 border-b border-recessed last:border-0">
            <div>
              <BodyText size="sm" className="font-medium">{pref.label}</BodyText>
              <BodyText size="xs" color="tertiary">{pref.description}</BodyText>
            </div>
            <Switch isSelected={prefs[pref.key]} onChange={() => toggle(pref.key)} />
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * ProfilePage - User-specific preferences and personal information.
 *
 * Accessible from the user menu "Profile" item. Distinct from platform
 * administration Settings which configure institution-level behavior.
 */
export function ProfilePage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'personal' | 'notifications' | 'security'>('personal');

  const tabs = [
    { id: 'personal' as const, label: 'Personal Info', icon: User },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'security' as const, label: 'Security', icon: Lock },
  ];

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Account details and preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-recessed mb-6">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 font-sans text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-secondary hover:text-primary'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'personal' && (
        <Card>
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-recessed">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-sans font-semibold text-accent">
                  {user?.name?.charAt(0) ?? 'U'}
                </span>
              </div>
              <div>
                <Heading level={3} size="compact" className="font-sans">{user?.name ?? 'User'}</Heading>
                <BodyText size="sm" color="secondary">{user?.roles?.[0] ?? 'N/A'}</BodyText>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <BodyText size="xs" color="tertiary" className="mb-1 uppercase tracking-wider font-semibold">Full Name</BodyText>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-tertiary" />
                  <Input value={user?.name ?? ''} readOnly className="flex-1" />
                </div>
              </div>
              <div>
                <BodyText size="xs" color="tertiary" className="mb-1 uppercase tracking-wider font-semibold">Email</BodyText>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-tertiary" />
                  <Input value={user?.email ?? ''} readOnly className="flex-1" />
                </div>
              </div>
              <div>
                <BodyText size="xs" color="tertiary" className="mb-1 uppercase tracking-wider font-semibold">Role</BodyText>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-tertiary" />
                  <Input value={user?.roles?.[0] ?? 'N/A'} readOnly className="flex-1" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-recessed">
              <BodyText size="xs" color="tertiary">
                {/* GH #1962: was "contact your administrator" -- a dead end, and a
                    wrong one: Aaim cannot change these. They are whatever your
                    identity provider says they are, so the only place a change
                    takes effect is there. */}
                Name and email come from your identity provider (the account you
                signed in with). Changing them there updates them here on your next
                sign-in.
              </BodyText>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <NotificationPreferences />
      )}

      {activeTab === 'security' && (
        <Card>
          <div className="p-6 space-y-6">
            <Heading level={3} size="compact" className="font-sans mb-4">Security</Heading>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-recessed">
                <div>
                  <BodyText size="sm" className="font-medium">Change Password</BodyText>
                  <BodyText size="xs" color="tertiary">Update your account password</BodyText>
                </div>
                <Button variant="secondary">Update</Button>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-recessed">
                <div>
                  <BodyText size="sm" className="font-medium">Two-Factor Authentication</BodyText>
                  <BodyText size="xs" color="tertiary">Add an extra layer of security</BodyText>
                </div>
                <Button variant="secondary">Configure</Button>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <BodyText size="sm" className="font-medium">Active Sessions</BodyText>
                  <BodyText size="xs" color="tertiary">Manage your active login sessions</BodyText>
                </div>
                <Button variant="secondary">View</Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
